import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
    Box,
    Typography,
    useTheme,
    Chip,
    Divider,
    Grid,
} from '@mui/material';
import { formatNumber, formatPrice } from '../utils/numberUtils';
import axios from '../config/axiosConfig';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const getDaysUntil = (dateStr) => {
    if (!dateStr) return null;
    const diff = Math.ceil((new Date(dateStr) - new Date()) / (1000 * 60 * 60 * 24));
    return diff > 0 ? diff : null;
};

const consensusConfig = {
    'Strong Buy': { color: '#00da3c' },
    'Buy': { color: '#00da3c' },
    'Hold': { color: '#f5a623' },
    'Sell': { color: '#ec0000' },
    'Strong Sell': { color: '#ec0000' },
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const SectionLabel = ({ children, theme }) => (
    <Typography
        variant="overline"
        sx={{
            color: theme.palette.primary.main,
            fontWeight: 700,
            fontSize: '0.68rem',
            letterSpacing: 1,
            display: 'block',
            mb: 0.5,
        }}
    >
        {children}
    </Typography>
);

const StatCell = ({ label, value, theme }) => {
    if (!value || value === 'N/A' || value === '-') return null;
    return (
        <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            py: 0.35,
        }}>
            <Typography variant="caption" sx={{
                color: theme.palette.text.secondary,
                fontSize: '0.72rem',
            }}>
                {label}
            </Typography>
            <Typography variant="caption" sx={{
                fontWeight: 700,
                fontSize: '0.75rem',
                color: theme.palette.text.primary,
            }}>
                {value}
            </Typography>
        </Box>
    );
};

const PriceRangeBar = ({ stockData, theme }) => {
    const { price, yearLow, yearHigh } = stockData;
    if (!price || !yearLow || !yearHigh) return null;

    const pct = Math.min(100, Math.max(0, ((price - yearLow) / (yearHigh - yearLow)) * 100));
    const isPositive = pct >= 50;

    return (
        <Box>
            <Divider sx={{ mb: 1 }} />
            <SectionLabel theme={theme}>52W Price Range</SectionLabel>
            <Box sx={{ px: 0.5 }}>
                <Box sx={{
                    position: 'relative',
                    height: 6,
                    borderRadius: 3,
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
                    mb: 1.5,
                }}>
                    <Box sx={{
                        position: 'absolute',
                        left: 0, top: 0,
                        width: `${pct}%`,
                        height: '100%',
                        borderRadius: 3,
                        backgroundColor: isPositive ? '#00da3c' : '#f5a623',
                        transition: 'width 0.6s ease',
                    }} />
                    <Box sx={{
                        position: 'absolute',
                        left: `${pct}%`,
                        top: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 12, height: 12,
                        borderRadius: '50%',
                        backgroundColor: isPositive ? '#00da3c' : '#f5a623',
                        border: `2px solid ${theme.palette.background.paper}`,
                        boxShadow: `0 0 0 2px ${isPositive ? '#00da3c' : '#f5a623'}`,
                    }} />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '0.68rem' }}>
                        L {formatPrice(yearLow)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: isPositive ? '#00da3c' : '#f5a623', fontWeight: 700, fontSize: '0.72rem' }}>
                        {pct.toFixed(1)}% of range
                    </Typography>
                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '0.68rem' }}>
                        H {formatPrice(yearHigh)}
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
};

const AnalystSection = ({ symbol, theme, stockData }) => {
    const [data, setData] = useState(null);
    const [buyPct, setBuyPct] = useState(0);
    const [holdPct, setHoldPct] = useState(0);
    const [sellPct, setSellPct] = useState(0);
    const [total, setTotal] = useState(0);

    useEffect(() => {
        axios.get(`/api/upgradesDowngradesConsensus/${symbol}`)
            .then(res => {
                const d = res.data.data;
                if (!d) return;
                setData(d);
                const t = d.buy + d.hold + d.sell;
                setTotal(t);
                setBuyPct(t > 0 ? (d.buy / t) * 100 : 0);
                setHoldPct(t > 0 ? (d.hold / t) * 100 : 0);
                setSellPct(t > 0 ? (d.sell / t) * 100 : 0);
            })
            .catch(() => { });
    }, [symbol]);

    if (!data) return (
        <Box sx={{ mt: 'auto', pt: 6 }}>
            <PriceRangeBar stockData={stockData} theme={theme} />
            <Box sx={{
                mt: 2,
                p: 1.5,
                borderRadius: 2,
                border: `1px dashed ${theme.palette.divider}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}>
                <Typography variant="caption" sx={{
                    color: theme.palette.text.disabled,
                    fontSize: '0.7rem',
                    fontStyle: 'italic',
                }}>
                    No analyst coverage available
                </Typography>
            </Box>
        </Box>
    );

    const consensus = data.consensus;
    const consensusStyle = consensusConfig[consensus] || { color: theme.palette.text.secondary };
    const buyRounded = Math.round(buyPct);
    const holdRounded = Math.round(holdPct);
    const sellRounded = 100 - buyRounded - holdRounded;

    return (
        <Box>
            <Divider sx={{ mb: 1 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <SectionLabel theme={theme}>Analyst Consensus</SectionLabel>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                        label={consensus}
                        size="small"
                        sx={{
                            backgroundColor: `${consensusStyle.color}22`,
                            color: consensusStyle.color,
                            fontWeight: 700,
                            fontSize: '0.68rem',
                            border: `1px solid ${consensusStyle.color}44`,
                            height: 18,
                            '& .MuiChip-label': { px: 0.75 },
                        }}
                    />
                    <Typography variant="caption" sx={{ color: theme.palette.text.disabled, fontSize: '0.68rem' }}>
                        {total} analysts
                    </Typography>
                </Box>
            </Box>

            <Box sx={{ display: 'flex', height: 7, borderRadius: 4, overflow: 'hidden', gap: '2px', mb: 1 }}>
                {buyPct > 0 && (
                    <Box sx={{ width: `${buyPct}%`, backgroundColor: '#00da3c', transition: 'width 0.6s ease', borderRadius: buyPct === 100 ? 4 : '4px 0 0 4px' }} />
                )}
                {holdPct > 0 && (
                    <Box sx={{ width: `${holdPct}%`, backgroundColor: '#f5a623', transition: 'width 0.6s ease' }} />
                )}
                {sellPct > 0 && (
                    <Box sx={{ width: `${sellPct}%`, backgroundColor: '#ec0000', transition: 'width 0.6s ease', borderRadius: sellPct === 100 ? 4 : '0 4px 4px 0' }} />
                )}
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                {[
                    { label: 'Buy', pct: buyRounded, color: '#00da3c' },
                    { label: 'Hold', pct: holdRounded, color: '#f5a623' },
                    { label: 'Sell', pct: sellRounded, color: '#ec0000' },
                ].map(({ label, pct, color }) => (
                    <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box sx={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: color }} />
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '0.7rem' }}>
                            {label} {pct}%
                        </Typography>
                    </Box>
                ))}
            </Box>

            {/* Always show price range bar below analyst section */}
            <PriceRangeBar stockData={stockData} theme={theme} />
        </Box>
    );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const StockStatsPanel = ({ stockData }) => {
    const theme = useTheme();
    const { symbol } = useParams();

    if (!stockData) return null;

    const daysUntilEarnings = getDaysUntil(stockData.earningsAnnouncement);
    const earningsDate = stockData.earningsAnnouncement
        ? new Date(stockData.earningsAnnouncement).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
        : null;

    const priceStats = [
        { label: 'Open', value: formatPrice(stockData.open) },
        { label: 'Prev Close', value: formatPrice(stockData.previousClose) },
        { label: 'Day High', value: formatPrice(stockData.dayHigh) },
        { label: 'Day Low', value: formatPrice(stockData.dayLow) },
        { label: '52W High', value: formatPrice(stockData.yearHigh) },
        { label: '52W Low', value: formatPrice(stockData.yearLow) },
        { label: 'MA 50', value: formatPrice(stockData.priceAvg50) },
        { label: 'MA 200', value: formatPrice(stockData.priceAvg200) },
    ].filter(s => s.value && s.value !== 'N/A' && s.value !== '-');

    const tradingStats = [
        { label: 'Market Cap', value: formatNumber(stockData.marketCap) },
        { label: 'Volume', value: formatNumber(stockData.volume) },
        { label: 'Avg Volume', value: formatNumber(stockData.avgVolume) },
        { label: 'EPS', value: stockData.eps?.toLocaleString() },
        { label: 'P/E Ratio', value: stockData.pe?.toLocaleString() },
        { label: 'Exchange', value: stockData.exchange },
    ].filter(s => s.value && s.value !== 'N/A' && s.value !== '-');

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>

            {/* ── Price Data ── */}
            <SectionLabel theme={theme}>Price Data</SectionLabel>
            <Grid container rowSpacing={0} sx={{ mb: 1 }}>
                {priceStats.map(({ label, value }) => (
                    <Grid item xs={6} key={label} sx={{ pr: 1, borderBottom: `1px solid ${theme.palette.divider}22` }}>
                        <StatCell label={label} value={value} theme={theme} />
                    </Grid>
                ))}
            </Grid>

            {/* ── Trading Stats ── */}
            <Divider sx={{ mb: 1 }} />
            <SectionLabel theme={theme}>Trading Stats</SectionLabel>
            <Grid container rowSpacing={0} sx={{ mb: 1 }}>
                {tradingStats.map(({ label, value }) => (
                    <Grid item xs={6} key={label} sx={{ pr: 1, borderBottom: `1px solid ${theme.palette.divider}22` }}>
                        <StatCell label={label} value={value} theme={theme} />
                    </Grid>
                ))}
            </Grid>

            {/* ── Next Earnings ── */}
            {earningsDate && (
                <>
                    <Divider sx={{ mb: 1 }} />
                    <SectionLabel theme={theme}>Next Earnings</SectionLabel>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        p: 1.25,
                        borderRadius: 2,
                        border: `1px solid ${theme.palette.primary.main}33`,
                        background: theme.palette.mode === 'dark'
                            ? 'linear-gradient(135deg, rgba(25,118,210,0.1) 0%, rgba(25,118,210,0.03) 100%)'
                            : 'linear-gradient(135deg, rgba(25,118,210,0.06) 0%, rgba(25,118,210,0.01) 100%)',
                        mb: 1,
                        position: 'relative',
                        overflow: 'hidden',
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            left: 0, top: 0,
                            width: '3px', height: '100%',
                            backgroundColor: theme.palette.primary.main,
                            borderRadius: '2px 0 0 2px',
                        }
                    }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.78rem', pl: 0.5 }}>
                            {earningsDate}
                        </Typography>
                        {daysUntilEarnings && (
                            <Chip
                                label={`In ${daysUntilEarnings}d`}
                                size="small"
                                sx={{
                                    backgroundColor: theme.palette.primary.main,
                                    color: '#fff',
                                    fontWeight: 700,
                                    fontSize: '0.65rem',
                                    height: 18,
                                    '& .MuiChip-label': { px: 0.75 },
                                }}
                            />
                        )}
                    </Box>
                </>
            )}

            {/* ── Analyst Consensus + Price Range ── */}
            <AnalystSection symbol={symbol} theme={theme} stockData={stockData} />
        </Box>
    );
};

export default StockStatsPanel;